/**
 * The automated enemies (02 §9.2). They are not soldiers, so they are kit models moved as
 * rigid meshes by `world/Machines`. Both are modelled facing +z, the sim's heading 0, and
 * both carry the enemy's colour where the camera sees it: on top.
 */

import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';

/** Where the mini-bunker's antenna foot sits when raised (live) and when retracted (dormant). */
export const ANTENNA_RAISED_Y = 0.9;
export const ANTENNA_RETRACTED_Y = -0.5;

/** Mini-bunker: a low armoured dome on a concrete skirt with a stubby gun and a sensor slot. */
export const miniBunkerBody: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const turn = Math.PI / 8; // a flat of the octagon, not a corner, carries the gun
  mb.cylinder(1.0, 0.9, 0.3, 8, p.color('concrete.lo'), place(0, 0, 0, turn), p.color('concrete.mid'));
  mb.cylinder(0.85, 0.62, 0.38, 8, p.color('metal.mid'), place(0, 0.3, 0, turn));
  mb.cylinder(0.64, 0.6, 0.1, 8, p.color('enemyAccent.0'), place(0, 0.68, 0, turn));
  mb.cylinder(0.6, 0.3, 0.2, 8, p.color('metal.mid'), place(0, 0.78, 0, turn), p.color('metal.hi'));
  mb.box(0.22, 0.2, 0.6, p.color('metal.lo'), place(0, 0.4, 0.85));
  // The sensor slot. Widened in round four: at 0.34 x 0.12 its glow was 6 x 1 px and read as a dot.
  mb.box(0.42, 0.17, 0.08, p.color('ink.0'), place(0, 0.58, 0.66));
  return mb;
};

/**
 * The antenna: up while the bunker is live, down while it is dormant - the tell the whole
 * SAM-site puzzle turns on, so the tip is a bold warm block rather than a realistic whip.
 * Origin at its foot; fully retracted, the tip sits flush with the dome.
 */
export const miniBunkerAntenna: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(0.09, 1.3, 0.09, p.color('metal.lo'));
  mb.box(0.2, 0.18, 0.2, p.color('fire.0'), place(0, 1.3, 0));
  return mb;
};

/**
 * The sensor slot, lit: drawn self-lit by `world/Machines` while the bunker is LIVE and not at all
 * while it sleeps. The antenna says "live" from above; this says it from the front, where the gun is,
 * and it is what makes a mini-bunker read the same way a manned concrete one does (ROUND4 §3).
 */
export const miniBunkerSlot: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  // 0.46 x 0.19 m: 8 px across and, foreshortened by the 45 degree camera, 2.4 px down - a bar, not a dot.
  mb.box(0.46, 0.19, 0.05, p.color('fire.1'), place(0, 0.57, 0.7));
  return mb;
};

/**
 * Minitank: a tracked box under a small turret. The heading has to read from above, so the
 * gun marks the front and a black engine deck, tail plate and exhaust stack mark the rear.
 */
export const minitankBody: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const track = { all: p.color('ink.1'), top: p.color('metal.lo') };
  for (const sx of [-1, 1]) mb.box(0.34, 0.5, 1.9, track, place(sx * 0.62, 0, 0));
  mb.box(0.95, 0.4, 1.6, { all: p.color('metal.mid'), top: p.color('metal.hi') }, place(0, 0.22, 0));
  mb.cylinder(0.42, 0.34, 0.3, 6, p.color('metal.mid'), place(0, 0.62, 0.1), p.color('metal.hi'));
  mb.box(0.5, 0.04, 0.16, p.color('enemyAccent.0'), place(0, 0.92, 0.1));
  mb.box(0.12, 0.12, 0.9, p.color('metal.lo'), place(0, 0.7, 0.85));
  mb.box(0.8, 0.05, 0.5, p.color('ink.0'), place(0, 0.62, -0.5));
  mb.box(0.9, 0.34, 0.1, p.color('ink.0'), place(0, 0.25, -0.82));
  mb.box(0.14, 0.42, 0.14, p.color('ink.1'), place(0.3, 0.62, -0.68));
  return mb;
};

/** Knocked-out mini-bunker: the skirt stands, the dome is split and burnt, the cap blown aside. */
export const miniBunkerWreck: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const turn = Math.PI / 8;
  mb.cylinder(1.0, 0.9, 0.3, 8, p.color('concrete.lo'), place(0, 0, 0, turn), p.color('ink.1'));
  mb.cylinder(0.85, 0.62, 0.34, 8, p.color('ink.1'), place(0.05, 0.26, 0, turn, 1, 0.08, 0.1), p.color('ink.0'));
  mb.cylinder(0.6, 0.3, 0.2, 8, p.color('metal.lo'), place(0.9, 0.05, 0.5, 0.4, 1, 0.5, 0.3), p.color('ink.1'));
  return mb;
};

/** Knocked-out minitank: hull burnt black and slewed on a shed track, turret blown off beside it. */
export const minitankWreck: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(0.34, 0.5, 1.9, p.color('ink.1'), place(-0.62, 0, 0));
  mb.box(0.34, 0.12, 2.2, p.color('ink.0'), place(0.8, 0, 0.2, 0.25));
  mb.box(0.95, 0.4, 1.6, { all: p.color('ink.1'), top: p.color('metal.lo') }, place(0, 0.12, 0, 0, 1, 0, -0.16));
  mb.cylinder(0.42, 0.34, 0.3, 6, p.color('ink.1'), place(-0.95, 0.02, -0.6, 0.7, 1, 0.3), p.color('ink.0'));
  return mb;
};
